/**
 * Postmaster V2 — Delivery-Errors (Ablehnungsgründe) section.
 *
 * Per-day breakdown of Gmail delivery errors by error_type (reject/temp_fail)
 * and error_reason, from postmaster_v2_delivery_errors (written daily by
 * postmaster_v2_daily_sync). This replaced V1's delivery_errors JSON when
 * Google shut the v1 API down on 2026-08-27.
 *
 * Interpretation: the reasons `low_domain_reputation` / `low_ip_reputation` /
 * `ip_in_rbls` / `spammy_content` are the only remaining API surface where
 * Gmail names reputation as a rejection CAUSE — visible only when mail is
 * actually rejected/deferred. This is NOT the retired 4-tier reputation
 * metric; a quiet table just means "nothing was rejected", not "reputation
 * is fine".
 *
 * Rows exist only for non-zero buckets — an empty result for a period is the
 * GOOD case. error_reason '' = aggregate over all reasons of that type.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { api } from '../../helpers/apiUtils';

interface DeliveryErrorApiRow {
  domain: string;
  stat_date: string;
  error_type: string;
  error_reason: string;
  error_count: number | null;
  error_rate: number | null;
  updated_at: string;
}

const ERROR_TYPE_LABELS: Record<string, string> = {
  reject: 'Abgelehnt (reject)',
  temp_fail: 'Temporär (temp_fail)',
};

const ERROR_REASON_LABELS: Record<string, string> = {
  '': 'Alle Gründe (Summe)',
  bad_attachment: 'Problematischer Anhang',
  bad_or_missing_ptr_record: 'PTR-Record fehlt/fehlerhaft',
  ip_in_rbls: 'IP auf Blocklisten (RBLs)',
  low_domain_reputation: 'Niedrige Domain-Reputation',
  low_ip_reputation: 'Niedrige IP-Reputation',
  spammy_content: 'Spam-verdächtiger Inhalt',
  stamp_policy_error: 'Stamp-Policy-Fehler',
  anomalous_traffic_pattern: 'Anomales Traffic-Muster',
  other: 'Sonstige',
};

// Reputation-CAUSE reasons get a highlighting chip — they answer the question
// the retired reputation charts used to.
const REPUTATION_REASONS = new Set([
  'low_domain_reputation',
  'low_ip_reputation',
  'ip_in_rbls',
  'spammy_content',
]);

function pct(v: number | null): string {
  if (v === null || Number.isNaN(v)) return '–';
  return `${(v * 100).toFixed(2)} %`.replace('.', ',');
}

interface PostmasterV2DeliveryErrorsTabProps {
  // Same contract as the sibling tabs: null = parent still loading (no
  // filter), [] = nothing selected, [..] = restrict to these domains.
  selectedDomains: string[] | null;
}

const PostmasterV2DeliveryErrorsTab: React.FC<PostmasterV2DeliveryErrorsTabProps> = ({
  selectedDomains,
}) => {
  const [rows, setRows] = useState<DeliveryErrorApiRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        // Default window (last 30 days) is applied server-side.
        const res = await api.get('/api/postmaster/v2/delivery-errors');
        if (cancelled) return;
        setRows(res.data.data || []);
      } catch (err) {
        if (!cancelled)
          setError(`Konnte Delivery-Errors nicht laden: ${(err as Error).message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredRows = useMemo(() => {
    if (selectedDomains === null) return rows;
    const set = new Set(selectedDomains);
    return rows.filter((r) => set.has(r.domain));
  }, [rows, selectedDomains]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (filteredRows.length === 0) {
    return (
      <Alert severity="success">
        Keine Zustellfehler-Einträge in den letzten 30 Tagen — Gmail hat in diesem Zeitraum keine
        nennenswerten Ablehnungen gemeldet. (Zeilen entstehen nur an Tagen mit Fehlern.)
      </Alert>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Ablehnungsgründe der letzten 30 Tage. Gründe wie „Niedrige Domain-/IP-Reputation" sind die
        einzige verbliebene Stelle, an der Gmail Reputation als Ursache benennt — sichtbar nur,
        wenn tatsächlich abgelehnt wurde.
      </Typography>
      <TableContainer sx={{ backgroundColor: '#ffffff', maxHeight: 420 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {['Datum', 'Domain', 'Typ', 'Grund', 'Anteil', 'Anzahl'].map((h) => (
                <TableCell
                  key={h}
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    color: 'black',
                    fontWeight: 'bold',
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.map((r, i) => (
              <TableRow key={`${r.domain}-${r.stat_date}-${r.error_type}-${r.error_reason}-${i}`}>
                <TableCell>{r.stat_date}</TableCell>
                <TableCell>{r.domain}</TableCell>
                <TableCell>{ERROR_TYPE_LABELS[r.error_type] ?? r.error_type}</TableCell>
                <TableCell>
                  {ERROR_REASON_LABELS[r.error_reason] ?? r.error_reason}
                  {REPUTATION_REASONS.has(r.error_reason) && (
                    <Chip
                      label="Reputations-Ursache"
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ ml: 1 }}
                    />
                  )}
                </TableCell>
                <TableCell>{pct(r.error_rate)}</TableCell>
                <TableCell>{r.error_count ?? '–'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default PostmasterV2DeliveryErrorsTab;
